import { useCallback } from 'react';
import { useSubdomain } from './useSubdomain';

export function useAssets() {
  const { subdomain } = useSubdomain();
  const assetBaseUrl = import.meta.env.VITE_ASSET_BASE_URL || 'https://assets.therize.net';
  const themeAssets = import.meta.env.VITE_R2_THEME_ASSETS || 'topic';

  // Determine if we should use subdomain-based or theme-based assets
  const useSubdomainAssets = themeAssets === 'topic';
  const themeName = useSubdomainAssets ? null : themeAssets;

  /**
   * Constructs the URL for an asset with fallback logic
   * @param {string} path - The path to the asset relative to the asset base URL
   * @returns {string} The complete asset URL
   */
  const getAssetUrl = useCallback((path) => {
    return `${assetBaseUrl}/${path}`;
  }, [assetBaseUrl]);

  /**
   * Gets a specific asset with theme override support
   * @param {string} category - The asset category (e.g., 'icons', 'backgrounds')
   * @param {string} filename - The filename of the asset
   * @returns {string} The complete asset URL
   */
  const getSpecificAsset = useCallback((category, filename) => {
    if (useSubdomainAssets) {
      return `${assetBaseUrl}/subdomains/${subdomain}/${category}/${filename}`;
    } else {
      return `${assetBaseUrl}/themes/${themeName}/${category}/${filename}`;
    }
  }, [assetBaseUrl, subdomain, useSubdomainAssets, themeName]);

  /**
   * Gets a subdomain-specific asset with fallback to common assets
   * This is maintained for backward compatibility
   * @param {string} category - The asset category (e.g., 'icons', 'backgrounds')
   * @param {string} filename - The filename of the asset
   * @returns {string} The complete asset URL
   */
  const getSubdomainAsset = useCallback((category, filename) => {
    return getSpecificAsset(category, filename);
  }, [getSpecificAsset]);

  /**
   * Gets a theme asset
   * @param {string} theme - The theme name
   * @param {string} category - The asset category (e.g., 'backgrounds', 'icons')
   * @param {string} filename - The filename of the asset
   * @returns {string} The complete asset URL
   */
  const getThemeAsset = useCallback((theme, category, filename) => {
    return `${assetBaseUrl}/themes/${theme}/${category}/${filename}`;
  }, [assetBaseUrl]);

  /**
   * Gets a common asset
   * @param {string} category - The asset category (e.g., 'ui', 'icons')
   * @param {string} filename - The filename of the asset
   * @returns {string} The complete asset URL
   */
  const getCommonAsset = useCallback((category, filename) => {
    return `${assetBaseUrl}/common/${category}/${filename}`;
  }, [assetBaseUrl]);

  return {
    getAssetUrl,
    getSpecificAsset,
    getSubdomainAsset,
    getThemeAsset,
    getCommonAsset
  };
}
